import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SelectionHeader.styled';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Button } from '@/components/UI/Button/Button';
import { Portal } from '@/components/UI/Portal/Portal';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { ButtonAppearance } from '@/components/UI/Button/Button.types';
import { useBookmarkSelectionStore } from '@/features/BookmarkCollections/store';
import { ConfirmDeleteModal } from '@/features/BookmarkCollections/components/ConfirmDeleteModal';

interface SelectionHeaderProps {
  title?: string;
  actionTitle: string;
  onActionSuccess?: () => void;
  actionAppearance?: ButtonAppearance;
  confirmAction?: {
    title: string;
    description: string;
  };
}

export const SelectionHeader = ({
  title = '',
  actionTitle,
  confirmAction,
  onActionSuccess,
  actionAppearance = 'primary',
}: SelectionHeaderProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const {
    isSelectionActive,
    selectedItems,
    setIsSelectionActive,
    clearSelection,
  } = useBookmarkSelectionStore();

  const headerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [isFixed, setIsFixed] = useState(false);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const selectedCount = selectedItems.length;
  const selectionTitle =
    selectedCount > 0
      ? t('bookmark_collections.selected_items_count', { count: selectedCount })
      : t('bookmark_collections.select_items');

  const handleDeselection = () => {
    setIsSelectionActive(false);
    clearSelection();
  };

  const handleActionClick = () => {
    if (confirmAction) {
      setIsConfirmDeleteOpen(true);
    } else {
      onActionSuccess?.();
      setIsActionSheetOpen(false);
    }
  };

  const ConfirmActionModal = useMemo(() => {
    return (
      <Portal>
        <ConfirmDeleteModal
          title={confirmAction?.title}
          description={confirmAction?.description}
          isOpen={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          onSubmit={() => onActionSuccess?.()}
        />
      </Portal>
    );
  }, [confirmAction, isConfirmDeleteOpen]);

  useEffect(() => {
    const trigger = triggerRef.current;
    const headerElement = headerRef.current?.offsetHeight;

    if (!isSelectionActive || !trigger || !headerElement) {
      setIsFixed(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(trigger);

    return () => {
      observer.unobserve(trigger);
    };
  }, [isSelectionActive]);

  useEffect(() => {
    setIsActionSheetOpen(isSelectionActive);
  }, [isSelectionActive]);

  if (!isDesktop && isSelectionActive) {
    return (
      <>
        <S.MobileHeader>
          <S.CloseButton
            onClick={handleDeselection}
            data-testid="deselect-button"
          >
            <CloseIcon width={15} height={15} />
          </S.CloseButton>
          <S.SelectionTitle>{selectionTitle}</S.SelectionTitle>
        </S.MobileHeader>

        {confirmAction && ConfirmActionModal}

        <Portal>
          <BottomSheet
            hasOverlay={false}
            isOpen={isActionSheetOpen}
            close={handleDeselection}
          >
            <S.MobileActionSheet>
              <Button
                isFullWidth
                borderRadius={14}
                disabled={!selectedCount}
                appearance={actionAppearance}
                onClick={handleActionClick}
                data-testid="selection-action-button"
              >
                {actionTitle}
              </Button>
            </S.MobileActionSheet>
          </BottomSheet>
        </Portal>
      </>
    );
  }

  if (isDesktop) {
    return (
      <>
        {isSelectionActive && (
          <>
            <div ref={triggerRef} style={{ height: '1px' }} />
            <S.HeaderPlaceholder
              $isFixed={isFixed}
              $height={headerRef.current?.offsetHeight ?? 0}
            />
          </>
        )}

        <S.HeaderBox ref={headerRef} $isFixed={isFixed}>
          <S.Header $isSelectionMode={isSelectionActive}>
            {isSelectionActive ? (
              <S.TitleWrapper>
                <S.CloseButton onClick={handleDeselection}>
                  <CloseIcon width={16} height={16} />
                </S.CloseButton>
                <S.SelectionTitle>{selectionTitle}</S.SelectionTitle>
              </S.TitleWrapper>
            ) : (
              <S.Title>{title}</S.Title>
            )}

            {isSelectionActive && (
              <S.ActionButton
                onClick={handleActionClick}
                disabled={!selectedCount}
                appearance={actionAppearance}
              >
                {actionTitle}
              </S.ActionButton>
            )}

            {confirmAction && ConfirmActionModal}
          </S.Header>
        </S.HeaderBox>
      </>
    );
  }
};
